import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { DataSourceUnknownKindError } from '@de/providers/DataSourceManager.types';
import type {
  FetchDataSourceOptions,
  FetchDataSourceResult,
} from './fetchDataSource';
import type { FetchInferenceOptions } from './fetchInferenceDataSource';
import type { FetchReportingModelOptions } from './fetchReportingModelDataSource';
import { readDeclaredKind } from './source-type-registry';
import { resolveDataSourceType } from './source-types';

export type DispatchFetchDataSourceOptions = FetchDataSourceOptions &
  FetchReportingModelOptions &
  FetchInferenceOptions;

/**
 * Route a declaration to its registered source type's fetcher.
 * An unregistered kind returns `status: 'error'` without throwing.
 */
export async function dispatchFetchDataSource(
  decl: DataSourceDeclaration,
  interpolationContext: unknown,
  options: DispatchFetchDataSourceOptions = {},
): Promise<FetchDataSourceResult> {
  const sourceType = resolveDataSourceType(decl);
  if (!sourceType) {
    // No endpoint or method is reported. A declaration that reaches here named
    // a kind nothing is registered under, so whatever `endpoint` it carries
    // was never going to be read as one.
    return {
      status: 'error',
      request: { endpoint: '', method: 'GET' },
      error: new DataSourceUnknownKindError(readDeclaredKind(decl)),
    };
  }

  // One request object. A caller that already built and coerced (the manager
  // does, so the cache key and the wire share one object) passes
  // `preparedRequest`. Everyone else builds here. Rebuilding after a coercer
  // with a nonce or timestamp would key one request and send another.
  const request =
    options.preparedRequest ??
    (options.coerceRequestBody
      ? options.coerceRequestBody(
          sourceType.buildRequest(decl, interpolationContext, options),
        )
      : sourceType.buildRequest(decl, interpolationContext, options));
  return sourceType.fetch(request, decl, options);
}
